import React from 'react';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import queryString from 'query-string';

import Product from '../product/ProductComponent';
import EmptyState from '../empty-state/EmptyStateComponent';
import SkeletonTiles from '../../common/skeleton-tiles/SkeletonTilesComponent';

import * as typeService from '../../services/product-type/ProductTypeService';
import { getProductIdByRoute } from '../../services/products-route/ProductsRouteService';

import './ProductsList.scss';
import AnalyticsClient from '../../../clients/AnalyticsClient';
import productEvents from '../../../analytics/events/products';

class ProductsListComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: {},
    };

    this.loadMore = this.loadMore.bind(this);
  }

  componentDidMount() {
    this.props.actions.productDeselectAllAction();

    AnalyticsClient.trackEvent(productEvents.typeSelect(getProductIdByRoute(this.props.match.params.type)));

    // if there is a filter set, it handles it as part of UNSAFE_componentWillReceiveProps
    if (!this.isFilterApplied()) {
      this.props.actions.productsClear();
      this.requestProducts(this.props);
    }
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    let filter;
    const oldProps = this.props;

    if (oldProps.match.params.type !== newProps.match.params.type) {
      newProps.actions.productsClear();
      newProps.actions.productDeselectAllAction();

      AnalyticsClient.trackEvent(productEvents.typeSelect(getProductIdByRoute(newProps.match.params.type)));
      filter = {}; // clear filters
    }

    // Change of filter
    const { productSize, productSort, template, searchTerm } = queryString.parse(newProps.location.search);

    // update state with new filters
    if (
      productSize !== this.state.filter.productSize ||
      productSort !== this.state.filter.productSort ||
      template !== this.state.filter.template ||
      searchTerm !== this.state.filter.searchTerm
    ) {
      newProps.actions.productsClear();
      filter = { productSize, productSort, template, searchTerm };
    }

    if (filter) {
      this.setState(
        {
          filter,
        },
        () => {
          newProps.actions.productDeselectAllAction();
          this.requestProducts(newProps);
        },
      );
    }
  }

  isFilterApplied() {
    const { productSize, productSort, template, searchTerm } = queryString.parse(this.props.location.search);
    return productSize || productSort || template || searchTerm;
  }

  sendRequestProductsApiCall() {
    this.props.actions.productsApiCall({
      method: 'GET',
      url: 'print-products',
      urlConfig: { sendStore: true },
      callback: this.props.actions.requestedProductsReceived,
    });
  }

  requestProducts(newProps) {
    const { productSize, productSort, template, searchTerm } = queryString.parse(newProps.location.search);

    newProps.actions.productSettings({
      productTypeId: newProps.isApprovalsView ? 'ALL' : getProductIdByRoute(newProps.match.params.type),
      start: 0,
      isLoading: true,
      productSize: typeService.getValidProductSize(productSize),
      productSort: typeService.getValidSort(productSort),
      searchTerm: searchTerm || '',
      template: template || 'ALL',
      approvalStatus: newProps.isApprovalsView ? 'PENDING' : '',
    });
    this.sendRequestProductsApiCall();

    newProps.actions.requestProductTemplates(
      getProductIdByRoute(newProps.match.params.type),
      newProps.runtimeConfig.api,
    );
  }

  loadMore() {
    if (this.props.hasMore) {
      this.props.actions.productSettings({ isLoading: true });
      this.sendRequestProductsApiCall();
    }
  }

  getActiveProductTiles() {
    const { items, isApprovalsView, actions, runtimeConfig, permissions } = this.props;

    const hasCheckedItems = items.filter((item) => item.isChecked).length > 0;
    const commonTileProps = {
      disableCTAs: hasCheckedItems,
      isApprovalsView,
      actions,
      runtimeConfig,
      permissions,
    };
    return items.map((item, index) => {
      const key = `product-list-item--${item.id}-${index}`;
      return <Product key={key} data={item} {...commonTileProps} />;
    });
  }

  hasVisibleTiles() {
    return this.props.items.some((product) => !product.isDeleted);
  }

  render() {
    const productTiles = this.getActiveProductTiles();
    const hasTiles = this.props.isLoading || this.hasVisibleTiles();

    if (hasTiles) {
      return (
        <div className="products-list-component">
          <InfiniteScroll
            data-test="product-list"
            dataLength={productTiles.length}
            className="products-list-component__layout"
            next={this.loadMore}
            hasMore={this.props.hasMore}
            style={{ overflow: 'inherit' }}
          >
            {productTiles}
            {this.props.isLoading && <SkeletonTiles amount={6} />}
          </InfiniteScroll>
        </div>
      );
    }
    // Finished loading with 0 visible products
    return <EmptyState {...this.props} />;
  }
}

ProductsListComponent.displayName = 'ProductsList';

ProductsListComponent.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      isChecked: PropTypes.bool,
      isDeleted: PropTypes.bool,
    }).isRequired,
  ),
  isApprovalsView: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  hasMore: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  productSize: PropTypes.string,
  productSort: PropTypes.string,
  searchTerm: PropTypes.string,
  template: PropTypes.string,
  actions: PropTypes.shape({
    productSettings: PropTypes.func.isRequired,
    productsApiCall: PropTypes.func.isRequired,
    productDeselectAllAction: PropTypes.func.isRequired,
    requestedProductsReceived: PropTypes.func.isRequired,
    requestProductTemplates: PropTypes.func.isRequired,
    productsClear: PropTypes.func.isRequired,
    showOverlay: PropTypes.func.isRequired,
  }),
  params: PropTypes.shape({
    type: PropTypes.string,
  }),
  location: PropTypes.shape({
    query: PropTypes.shape({
      productSize: PropTypes.string,
      productSort: PropTypes.string,
      searchTerm: PropTypes.string,
      template: PropTypes.string,
    }),
  }),
  runtimeConfig: PropTypes.shape({
    api: PropTypes.string.isRequired,
  }).isRequired,
};

ProductsListComponent.defaultProps = {
  location: {
    query: {},
  },
};

export default ProductsListComponent;
